import { CANVAS_GRID_PX, ZOOM_MIN, type ViewState } from "../../lib/canvas-coordinates";
import { useCanvasStore } from "./store/canvas-store";

// キャンバス画面全体（ビューポート）に、白い下地とグリッド点を描く。
// PDF 出力範囲（キャンバス矩形）の内外で下地とグリッドを連続させ、範囲の境界は
// PdfBoundaryOverlay の点線と注釈だけで示す。
//
// 各点をひとつずつ描画するのではなく、CSS の radial-gradient と background-size を使いパフォーマンスを稼ぐ。
// ステージ（transform: scale）の内側に巨大な面を置く代わりに、ビューポートと同じ大きさの 1 枚の層へ
// 白い下地を敷き、その中の点の層でステージ座標の 20px 格子（CANVAS_GRID_PX）を pan / zoom で画面へ投影する。
// 無限に広い DOM や巨大な画像を作らずに、どこまでパンしても同じ格子が続く。
//
// 点の層は、ステージ座標の 20px のタイル（点の半径 1px）を transform: scale(zoom) で zoom 倍にして描く。
// 画面での周期（20 × zoom）を background-size へそのまま渡すと、ブラウザがタイルの寸法を内部の精度
// （Chromium では 1/64px）へ丸め、その誤差がタイルごとに積み重なって、層の左上から離れるほど点が
// グリッド吸着の位置からずれる（実測: 倍率 66.7% で幅 1920px の右端が約 1.7px）。タイルの寸法を整数の
// 20px に保てば、各点の画面位置は「層の座標 × zoom」で求まり、誤差が積み重ならない。
// 点の層の大きさ（画面の大きさ ÷ 倍率）は倍率が小さいほど大きくなり、極端に小さい倍率ではブラウザの長さの
// 上限（Chromium で約 3,355 万 px）を超えて切り詰められ、画面を覆えなくなる。ズーム操作の範囲（ZOOM_MIN =
// 5% 以上）では画面の 20 倍以内に収まる。それより小さい倍率（共有 URL の z など、丸めずに適用する倍率）では
// 点の間隔が 1px 未満になって格子として見分けられないため、点の層を描かない（白い下地だけを描く）。
// 下地とグリッドの色は作品用の固定トークン（styles.css の --color-canvas-artwork-*）で、
// 画面のテーマに関わらず PDF と同じライト配色のまま描く。

/** グリッド点の半径（ステージ座標）。層ごと zoom 倍にするため、画面では図形と同じ倍率で大きさが変わる。 */
const GRID_DOT_RADIUS_PX = 1;
/** 点の層の大きさ（ステージ座標）へ足す余白（2 周期ぶん）。zoom 倍にした後の丸めで画面の端に隙間を作らない。 */
const GRID_LAYER_MARGIN_PX = CANVAS_GRID_PX * 2;

export function CanvasGrid(): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const dots = resolveViewportGridDots(view);
  return (
    <div aria-hidden data-testid="canvas-viewport-grid" className="canvas-viewport-grid">
      {dots.visible ? (
        <div
          data-testid="canvas-viewport-grid-dots"
          className="canvas-viewport-grid-dots"
          style={{
            // zoom 倍にする前の大きさ（ステージ座標）。zoom 倍にした後に画面全体を覆う。
            width: `calc(100% / ${dots.scale} + ${GRID_LAYER_MARGIN_PX}px)`,
            height: `calc(100% / ${dots.scale} + ${GRID_LAYER_MARGIN_PX}px)`,
            transform: `scale(${dots.scale})`,
            backgroundImage: `radial-gradient(circle, var(--color-canvas-artwork-grid-dot) ${GRID_DOT_RADIUS_PX}px, transparent ${GRID_DOT_RADIUS_PX}px)`,
            backgroundSize: `${CANVAS_GRID_PX}px ${CANVAS_GRID_PX}px`,
            backgroundPosition: `${dots.positionXPx}px ${dots.positionYPx}px`,
          }}
        />
      ) : null}
    </div>
  );
}

/** 点の層の寸法。位置はいずれも zoom 倍にする前の層の座標（ステージ座標と同じ尺度の CSS px）。 */
export interface ViewportGridDots {
  /** 点の層を描くか（倍率が ZOOM_MIN より小さいときは描かない）。 */
  readonly visible: boolean;
  /** 点の層に掛ける倍率（= zoom）。 */
  readonly scale: number;
  /** background-position の x。 */
  readonly positionXPx: number;
  /** background-position の y。 */
  readonly positionYPx: number;
}

/**
 * pan / zoom から、ステージ座標の 20px 格子（グリッド吸着の位置）に点が載る点の層の寸法を求める。
 *
 * 点の層は画面の左上（0, 0）を原点に zoom 倍にするため、層の座標でのステージ原点の位置は pan / zoom になる。
 * radial-gradient は位置省略時タイル中央にドットを描くため、そのままではドットが半セルずれる。
 * タイルの原点を「点の位置 − 半周期（10px）」に合わせる。pan / zoom を周期（20px）で割った余りを 0 以上へ
 * 正規化してから半周期を引くことで、負の pan でも位相がずれず、格子点（20 の倍数のステージ座標。
 * グリッド吸着の位置）が常に点の中心と一致する。
 */
export function resolveViewportGridDots(view: ViewState): ViewportGridDots {
  const scale = view.zoom > 0 && Number.isFinite(view.zoom) ? view.zoom : 1;
  return {
    visible: scale >= ZOOM_MIN,
    scale,
    positionXPx: normalizePhase(view.panX / scale, CANVAS_GRID_PX) - CANVAS_GRID_PX / 2,
    positionYPx: normalizePhase(view.panY / scale, CANVAS_GRID_PX) - CANVAS_GRID_PX / 2,
  };
}

function normalizePhase(value: number, period: number): number {
  if (!(period > 0) || !Number.isFinite(value)) return 0;
  return ((value % period) + period) % period;
}
